// services/api.js
const API_BASE_URL = '/api'
// توکن پنل ادمین — در localStorage می‌ماند و روی همهٔ درخواست‌های /admin هدر می‌شود
const ADMIN_TOKEN_KEY = 'za_admin_token'
class ApiService {
    constructor() {
        this.baseURL = API_BASE_URL
        // کد وضعیت HTTP آخرین درخواست (0 = خطای شبکه) — ویوهای جزئیات با آن
        // «404 واقعی» را از «خطای سرور/شبکه» تفکیک می‌کنند تا noindex فقط
        // برای URLهای واقعاً ناموجود زده شود، نه هنگام قطعی API/دیتابیس
        this.lastStatus = 0
    }

    // ---- توکن ادمین ----
    getAdminToken() {
        try { return localStorage.getItem(ADMIN_TOKEN_KEY) || '' } catch { return '' }
    }

    setAdminToken(token) {
        try {
            if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token)
            else localStorage.removeItem(ADMIN_TOKEN_KEY)
        } catch { /* حالت خصوصی مرورگر */ }
    }

    async request(endpoint, options = {}) {
        try {
            const url = `${this.baseURL}${endpoint}`
            const headers = {
                'Content-Type': 'application/json',
                ...options.headers
            }
            // هدر توکن فقط برای مسیرهای ادمین — توکن از localStorage
            if (this.getAdminToken() && endpoint.startsWith('/admin')) {
                headers['x-admin-token'] = this.getAdminToken()
            }
            const response = await fetch(url, {
                headers,
                ...options
            })

            if (!response.ok) {
                const err = new Error(`HTTP error! status: ${response.status}`)
                err.status = response.status
                throw err
            }
            this.lastStatus = response.status
            return await response.json()
        } catch (error) {
            // 0 = خطای شبکه، 5xx = خطای سرور، 404 = واقعاً ناموجود
            this.lastStatus = error && typeof error.status === 'number' ? error.status : 0
            console.error('API request failed:', error)
            // بازگشت به داده تستی در صورت خطا
            // return this.getFallbackData(endpoint)
        }
    }

    // نسخهٔ سخت‌گیرانه — فقط برای پنل ادمین: خطا را می‌بلعد که caller ببیند.
    // چرا؟ request() عادی در catch دیتای undefined برمی‌گرداند (فیل-سافت ویوها)؛
    // اگر ادمین هم از آن می‌رفت، checkToken بعد از هر خطا resolved می‌شد و
    // هر توکن الکی «وارد» می‌شد (باگ فیل-اُپن واقعی که کاربر دید).
    async requestStrict(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        }
        if (this.getAdminToken() && endpoint.startsWith('/admin')) {
            headers['x-admin-token'] = this.getAdminToken()
        }
        const response = await fetch(url, { headers, ...options })
        if (!response.ok) {
            const err = new Error(`HTTP error! status: ${response.status}`)
            err.status = response.status
            throw err
        }
        this.lastStatus = response.status
        return response.json()
    }

    // ⏱️ محافظ ضد noindex گذرا — برای صفحات جزئیات (خبر/بازی/تیم/بازیکن/مربی)
    // اگر درخواست اول 404 آمد، یک‌بار با تأخیر کوتاه تلاش مجدد می‌شود تا
    // هیچ حالت گذرایی (پنجرهٔ بوت سرور، فشار لحظه‌ای DB، قطعی کوتاه) URL سالم
    // را noindex نکند — دقیقاً همان خطایی که گوگل برای یک خبر سالم «Excluded
    // by noindex» زد. 404 پایدار (محتوای واقعاً حذف‌شده) بعد از تلاش دوم
    // همچنان 404 برمی‌گردد و ویو noindex می‌زند — رفتار درست حفظ شده است.
    async requestWithNotFoundRetry(endpoint, delayMs = 1200) {
        let res = await this.request(endpoint)
        if ((res === null || res === undefined) && this.lastStatus === 404) {
            await new Promise((resolve) => setTimeout(resolve, delayMs))
            res = await this.request(endpoint)
        }
        return res
    }

    // لیگ‌ها
    async getLeagues() {
        return this.request('/leagues')
    }

    async getLeagueById(id) {
        return this.request(`/leagues/${id}`)
    }

    // تیم‌ها
    async getTeams() {
        return this.request('/teams')
    }

    async getTeamById(id) {
        return this.request(`/teams/${id}`)
    }

    // جستجوی تیم (انتخابگر مقایسه)
    async searchTeams(q, take = 8) {
        return this.request(`/teams/search?q=${encodeURIComponent(q)}&take=${take}`)
    }

    // مقایسه دو تیم (a و b: id یا slug)
    async compareTeams(a, b) {
        return this.request(`/teams/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`)
    }

    // آمار فصل یک تیم (گلزنان/پاسورها + سطر جدول)
    async getTeamStats(key) {
        return this.request(`/teams/${key}/stats`)
    }

    // برترین بازیکنان یک رقابت از رویدادهای محلی
    async getCompetitionTopPlayers(id, seasonId = null) {
        const q = seasonId ? `?seasonId=${seasonId}` : ''
        return this.request(`/competition/${id}/top-players${q}`)
    }

    // مسابقات
    async getMatches({
                         date = null,
                         competitionId = null,
                         seasonId = null,
                         status = null
                     } = {}) {

        const params = new URLSearchParams()

        if (date) {
            params.append('date', date)
        }

        if (competitionId) {
            params.append('competitionId', competitionId)
        }

        if (seasonId) {
            params.append('seasonId', seasonId)
        }

        if (status) {
            params.append('status', status)
        }

        const query = params.toString()

        return this.request(
            `/matches${query ? `?${query}` : ''}`
        )
    }

    // مسابقات امروز
    async getTodayMatches() {
        return this.request('/matches/today')
    }

    async getLiveMatches() {
        return this.request('/matches/live')
    }

    async getMatchById(id) {
        return this.request(`/matches/${id}`)
    }

    // بازیکنان
    async getPlayers() {
        return this.request('/players')
    }

    async getPlayerById(id) {
        return this.request(`/players/${id}`)
    }

    // اخبار
    async getNews({ page = 1, limit = 20, source = null, search = null } = {}) {
        const params = new URLSearchParams({ page, limit })
        if (source) params.append('source', source)
        if (search) params.append('search', search)
        return this.request(`/news?${params.toString()}`)
    }

    async getHotNews() {
        return this.request('/news/hot')
    }

    async getNewsById(id) {
        return this.request(`/news/${id}`)
    }

    async getNewsSources() {
        return this.request('/news/sources')
    }

    async triggerNewsFetch() {
        return this.request('/news/fetch', { method: 'POST' })
    }

    // ویدئوها
    async searchVideos(query) {
        return this.request(`/videos/search?q=${encodeURIComponent(query)}`)
    }

    // جدول لیگ
    async getLeagueTable(leagueId) {
        console.log('[API Service] ارسال به بک‌اند:', leagueId);
        return this.request(`/sports/leagues/${leagueId}/table`);
    }

    async getAvailableLeagues() {
        return this.request('/sports/leagues');
    }

    // داده تستی برای وقتی API در دسترس نیست
    getFallbackData(endpoint) {
        const fallbackData = {
            '/leagues': [
                { id: 1, name: 'لیگ برتر ایران', country: 'ایران' },
                { id: 2, name: 'لیگ قهرمانان اروپا', country: 'اروپا' }
            ],
            '/teams': [
                { id: 1, name: 'پرسپولیس', leagueId: 1, logo: '' },
                { id: 2, name: 'استقلال', leagueId: 1, logo: '' }
            ],
            '/matches': [
                {
                    id: 1,
                    leagueId: 1,
                    team1Id: 1,
                    team2Id: 2,
                    score: '2-1',
                    status: 'finished',
                    team1: { name: 'پرسپولیس' },
                    team2: { name: 'استقلال' }
                }
            ],
            '/news': [
                {
                    id: 1,
                    title: 'پیروزی پرسپولیس در شهرآورد',
                    summary: 'شکست استقلال با نتیجه ۲-۱',
                    category: 'فوتبال داخلی',
                    imageUrl: '',
                    views: 12543
                }
            ]
        }

        return fallbackData[endpoint] || []
    }

    // آمار و دسته‌بندی
    async getPopularNews(limit = 8) {
        const res= this.request(`/stats/popular?limit=${limit}`);
        return res.data;

    }

    async getCategories() {
        return this.request('/stats/categories')
    }

    async getTags() {
        return this.request('/stats/tags')
    }

// اخبار ادمین
    async adminCreateNews(newsData) {
        return this.requestStrict('/admin/news', {
            method: 'POST',
            body: JSON.stringify(newsData)
        });
    }

    async adminGetNews() {
        return this.requestStrict('/admin/news');
    }

    async adminUpdateNews(id, newsData) {
        return this.requestStrict(`/admin/news/${id}`, {
            method: 'PUT',
            body: JSON.stringify(newsData)
        });
    }

    async adminDeleteNews(id) {
        return this.requestStrict(`/admin/news/${id}`, {
            method: 'DELETE'
        });
    }

    // آمار ادمین
    async adminGetStats() {
        return this.requestStrict('/admin/stats');
    }

    // ============ دانستنی‌ها (عمومی) ============
    // فکت‌های منتشرشده — صفحهٔ دانستنی‌ها با فالبک استاتیک می‌خواند
    async getFacts() {
        return this.request('/facts');
    }

    // یک فکت برای صفحهٔ اختصاصی — 404 واقعی → null (notFound سئو)
    async getFact(slug) {
        return this.requestWithNotFoundRetry('/facts/' + encodeURIComponent(slug));
    }

    // ============ «دانستنیِ روز» (ادمین) ============
    async adminDailyStatus() {
        return this.requestStrict('/admin/facts/daily-status');
    }

    async adminDailyRun({ force = false, dryRun = false } = {}) {
        return this.requestStrict('/admin/facts/daily-run', {
            method: 'POST',
            body: JSON.stringify({ force, dryRun })
        });
    }

    // ============ دانستنی‌ها (ادمین) ============
    async adminGetFacts() {
        return this.requestStrict('/admin/facts');
    }

    async adminCreateFact(data) {
        return this.requestStrict('/admin/facts', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async adminUpdateFact(id, data) {
        return this.requestStrict(`/admin/facts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async adminDeleteFact(id) {
        return this.requestStrict(`/admin/facts/${id}`, {
            method: 'DELETE'
        });
    }

    // آپلود عکس فکت — dataURL (base64) → مسیر عمومی /uploads/facts/...
    async adminUploadFactImage(dataUrl) {
        return this.requestStrict('/admin/facts/image', {
            method: 'POST',
            body: JSON.stringify({ data: dataUrl })
        });
    }

    // ============ رویدادهای ویژه (ادمین) ============
    async adminGetEvents() {
        return this.requestStrict('/admin/events');
    }

    async adminCreateEvent(data) {
        return this.requestStrict('/admin/events', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async adminUpdateEvent(id, data) {
        return this.requestStrict(`/admin/events/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async adminDeleteEvent(id) {
        return this.requestStrict(`/admin/events/${id}`, {
            method: 'DELETE'
        });
    }

    // ============ سینک فوتبال (ادمین) ============
    async adminGetSyncStatus() {
        return this.requestStrict('/admin/sync-status');
    }

    async adminTriggerSync(job) {
        return this.requestStrict(`/admin/sync/trigger/${encodeURIComponent(job)}`, {
            method: 'POST'
        });
    }

    async adminGetTagStatus() {
        return this.requestStrict('/admin/tag-status');
    }

    // لیگ‌ها
    async getCompetitions() {
        return this.request('/api/competition')
    }

    async getTopLeagues() {
        return this.request('/competition/top')
    }

    // مسابقات امروز
}


export default new ApiService()